import { useQuery } from "@tanstack/react-query";

import { listSites } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AppError } from "@/lib/errors";
import type { ListSitesResp } from "@/lib/types";

/**
 * Admin-vs-buyer signal shared by the sidebar (which nav items to show) and
 * `AdminRoute` (which routes to gate). Piggybacks on the `listSites` query —
 * the cheapest admin callable — so all three consumers (this hook, the sites
 * list page, the command bar) share one cached result under the same
 * queryKey instead of firing separate round trips.
 *
 * `isAdmin` is false only on a confirmed `permission-denied` (buyer). Any
 * other error resolves `isAdmin: true` so a real admin isn't bounced by a
 * transient failure — the page's own query surfaces that error instead.
 */
export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const { user } = useAuth();

  const gate = useQuery<ListSitesResp>({
    queryKey: ["sites"],
    queryFn: listSites,
    enabled: Boolean(user),
  });

  if (!user) return { isAdmin: false, isLoading: false };
  if (gate.isLoading) return { isAdmin: false, isLoading: true };
  if (gate.error instanceof AppError && gate.error.isPermissionDenied) {
    return { isAdmin: false, isLoading: false };
  }
  return { isAdmin: true, isLoading: false };
}
